import type { NavCollapsible, NavItem } from './types';

/**
 * Sidebar active matching: exact path or a strict descendant
 * (`itemUrl/` + more). Sibling segments under the same parent
 * (e.g. `/workspace/billing` vs `/workspace/locations`) never match.
 */
export function isNavPathActive(pathname: string, itemUrl: string): boolean {
  const cleanPathname = pathname.split('?')[0] ?? pathname;
  // Legacy locale prefix (e.g. `/en/...`) — shell paths are `/tabs/...` today.
  const normalizedPath = cleanPathname.replace(/^\/[a-z]{2}(\/|$)/, '/');
  const normalizedItemUrl = itemUrl.startsWith('/') ? itemUrl : `/${itemUrl}`;

  return (
    normalizedPath === normalizedItemUrl ||
    normalizedPath.startsWith(`${normalizedItemUrl}/`)
  );
}

/**
 * Sidebar item active state: path (plus `activePrefixes` / `excludePrefixes`)
 * and, for items whose url carries a query, every query param must match.
 */
export function checkIsActive(
  pathname: string,
  item: NavItem | NavCollapsible['items'][number],
  search = '',
): boolean {
  if (!('url' in item) || !item.url) {
    if ('items' in item && item.items) {
      return item.items.some((subItem) =>
        checkIsActive(pathname, subItem, search),
      );
    }
    return false;
  }

  const [itemPath = '', itemQuery = ''] = item.url.split('?');
  const excludePrefixes =
    'excludePrefixes' in item ? item.excludePrefixes : undefined;
  if (excludePrefixes?.some((prefix) => isNavPathActive(pathname, prefix))) {
    return false;
  }

  const pathMatches =
    isNavPathActive(pathname, itemPath) ||
    ('activePrefixes' in item && item.activePrefixes
      ? item.activePrefixes.some((prefix) => isNavPathActive(pathname, prefix))
      : false);

  if (!pathMatches) return false;

  if (itemQuery) {
    const required = new URLSearchParams(itemQuery);
    const actual = new URLSearchParams(
      search.startsWith('?') ? search.slice(1) : search,
    );
    for (const [key, value] of required.entries()) {
      if (actual.get(key) !== value) return false;
    }
    return true;
  }

  // A plain path stays active across its in-page `?tab=` views (People
  // Teams / Rollen, DEV-538); only items that carry a query are query-bound.

  return true;
}
